import React from 'react';
import { CloseButton } from '../common/CloseButton';
import './SongSettingsPanel.css';

/**
 * Props for SongSettingsPanel component
 * 
 * Requirements: 6.6
 */
export interface SongSettingsPanelProps {
  /** Current BPM value */
  bpm: number;
  /** Current steps value */
  steps: number;
  /** Callback when BPM button is tapped */
  onBPMTap: () => void;
  /** Callback when Steps button is tapped */
  onStepsTap: () => void;
  /** Callback when AI Generate button is tapped */
  onAIGenerate: () => void;
  /** Callback when Templates button is tapped */
  onTemplates: () => void;
  /** Callback when Save button is tapped */
  onSave: () => void;
  /** Callback when Share button is tapped */
  onShare: () => void;
  /** Callback when Export button is tapped */
  onExport: () => void;
  /** Callback when Reset button is tapped */
  onReset: () => void;
  /** Callback when panel is closed */
  onClose: () => void;
}

/**
 * SongSettingsPanel Component
 * 
 * Full-screen panel for song-level settings and actions.
 * Provides access to BPM, Steps, Save, Share, Export, and Reset.
 * 
 * Requirements: 6.6
 */
export const SongSettingsPanel: React.FC<SongSettingsPanelProps> = ({
  bpm,
  steps,
  onBPMTap,
  onStepsTap,
  onAIGenerate,
  onTemplates,
  onSave,
  onShare,
  onExport,
  onReset,
  onClose,
}) => {
  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className="song-settings-panel-backdrop" 
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Panel container */}
      <div 
        className="song-settings-panel" 
        role="dialog" 
        aria-label="Song settings"
        aria-modal="true"
      >
        {/* Header with close button */}
        <div className="song-settings-panel-header">
          <h2 className="song-settings-panel-title">Song Settings</h2>
          <CloseButton
            onClick={onClose}
            ariaLabel="Close song settings"
            size="medium"
            variant="default"
          />
        </div>

        {/* Content area */}
        <div className="song-settings-panel-content">
          {/* AI & Templates Section */}
          <div className="song-settings-section">
            <h3 className="song-settings-section-title">
              <i className="fa-solid fa-wand-magic-sparkles"></i>
              Create
            </h3>
            <div className="song-settings-section-content">
              <button
                className="song-settings-button song-settings-button-primary"
                onClick={onAIGenerate}
                aria-label="Generate with AI"
              >
                <div className="song-settings-button-icon">
                  <i className="fa-solid fa-sparkles"></i>
                </div>
                <div className="song-settings-button-content">
                  <span className="song-settings-button-label">AI Generate</span>
                  <span className="song-settings-button-description">Create music with AI</span>
                </div>
                <i className="fa-solid fa-chevron-right song-settings-button-arrow"></i>
              </button>

              <button
                className="song-settings-button"
                onClick={onTemplates}
                aria-label="Browse templates"
              >
                <div className="song-settings-button-icon">
                  <i className="fa-solid fa-layer-group"></i>
                </div>
                <div className="song-settings-button-content">
                  <span className="song-settings-button-label">Templates</span>
                  <span className="song-settings-button-description">Start from a template</span>
                </div>
                <i className="fa-solid fa-chevron-right song-settings-button-arrow"></i>
              </button>
            </div>
          </div>

          {/* Composition Settings Section */}
          <div className="song-settings-section">
            <h3 className="song-settings-section-title">
              <i className="fa-solid fa-sliders"></i>
              Composition Settings
            </h3>
            <div className="song-settings-section-content">
              <button
                className="song-settings-button"
                onClick={onBPMTap}
                aria-label={`Change BPM (currently ${bpm})`}
              >
                <div className="song-settings-button-icon">
                  <i className="fa-solid fa-gauge-high"></i>
                </div>
                <div className="song-settings-button-content">
                  <span className="song-settings-button-label">Tempo (BPM)</span>
                  <span className="song-settings-button-value">{bpm}</span>
                </div>
                <i className="fa-solid fa-chevron-right song-settings-button-arrow"></i>
              </button>

              <button
                className="song-settings-button"
                onClick={onStepsTap}
                aria-label={`Change steps (currently ${steps})`}
              >
                <div className="song-settings-button-icon">
                  <i className="fa-solid fa-list-ol"></i>
                </div>
                <div className="song-settings-button-content">
                  <span className="song-settings-button-label">Pattern Length</span>
                  <span className="song-settings-button-value">{steps} steps</span>
                </div>
                <i className="fa-solid fa-chevron-right song-settings-button-arrow"></i>
              </button>
            </div>
          </div>

          {/* Actions Section */}
          <div className="song-settings-section">
            <h3 className="song-settings-section-title">
              <i className="fa-solid fa-bolt"></i>
              Actions
            </h3>
            <div className="song-settings-section-content">
              <button
                className="song-settings-button"
                onClick={onSave}
                aria-label="Save composition"
              >
                <div className="song-settings-button-icon">
                  <i className="fa-solid fa-floppy-disk"></i>
                </div>
                <div className="song-settings-button-content">
                  <span className="song-settings-button-label">Save</span>
                  <span className="song-settings-button-description">Save to cloud</span>
                </div>
                <i className="fa-solid fa-chevron-right song-settings-button-arrow"></i>
              </button>

              <button
                className="song-settings-button"
                onClick={onShare}
                aria-label="Share composition"
              >
                <div className="song-settings-button-icon">
                  <i className="fa-solid fa-share-nodes"></i>
                </div>
                <div className="song-settings-button-content">
                  <span className="song-settings-button-label">Share</span>
                  <span className="song-settings-button-description">Get shareable link</span>
                </div>
                <i className="fa-solid fa-chevron-right song-settings-button-arrow"></i>
              </button>

              <button
                className="song-settings-button"
                onClick={onExport}
                aria-label="Export as WAV"
              >
                <div className="song-settings-button-icon">
                  <i className="fa-solid fa-download"></i>
                </div>
                <div className="song-settings-button-content">
                  <span className="song-settings-button-label">Export</span>
                  <span className="song-settings-button-description">Download as WAV</span>
                </div>
                <i className="fa-solid fa-chevron-right song-settings-button-arrow"></i>
              </button>

              <button
                className="song-settings-button song-settings-button-danger"
                onClick={onReset}
                aria-label="Reset composition"
              >
                <div className="song-settings-button-icon">
                  <i className="fa-solid fa-rotate-left"></i>
                </div>
                <div className="song-settings-button-content">
                  <span className="song-settings-button-label">Reset</span>
                  <span className="song-settings-button-description">Clear everything</span>
                </div>
                <i className="fa-solid fa-chevron-right song-settings-button-arrow"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
